import InputField from "components/fields/InputField";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { _vendorRegister } from "Services/api";
import SelectField from "components/fields/SelectField";
import {
  SignUpFormFieldsName,
  signUpFormDefaultValues,
  signUpFormSchema,
} from "constants/react-hook-form";
import { storeAdminSession } from "utils/session";

export default function SignUp() {
  const [apiResError, setApiResError] = useState();

  const { control, handleSubmit, formState, setError } = useForm({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: signUpFormDefaultValues,
  });

  const handleVendorRegister = async (data) => {
    try {
      // Clear previous errors
      setApiResError(null);

      const body = {
        first_name: data[SignUpFormFieldsName.FIRST_NAME],
        last_name: data[SignUpFormFieldsName.LAST_NAME],
        email: data[SignUpFormFieldsName.EMAIL],
        country_code: data[SignUpFormFieldsName.COUNTRY_CODE],
        phone_number: data[SignUpFormFieldsName.PHONE],
        password: data[SignUpFormFieldsName.PASSWORD],
        type: "VENDOR",
      };
      const response = await _vendorRegister({ body });
      if (response.status) {
        const responseData = response?.data?.data || response?.data || {};
        storeAdminSession(responseData);

        window.location.href = "/admin/auth/organization";
      }
    } catch (error) {
      console.log(error);

      // Handle nested error structure
      const errorData = error?.response?.data;

      if (errorData?.message?.errors && Array.isArray(errorData.message.errors)) {
        // Set field-specific errors
        errorData.message.errors.forEach((err) => {
          const fieldName = err.field;
          const errorMessage = err.message;

          // Map API field names to form field names
          const fieldMap = {
            phone_number: SignUpFormFieldsName.PHONE,
            email: SignUpFormFieldsName.EMAIL,
            password: SignUpFormFieldsName.PASSWORD,
            first_name: SignUpFormFieldsName.FIRST_NAME,
            last_name: SignUpFormFieldsName.LAST_NAME,
            country_code: SignUpFormFieldsName.COUNTRY_CODE,
          };

          const formFieldName = fieldMap[fieldName];
          if (formFieldName) {
            setError(formFieldName, {
              type: "server",
              message: errorMessage,
            });
          }
        });
      } else if (errorData?.message) {
        // If it's a general error message
        setApiResError(
          typeof errorData.message === "string"
            ? errorData.message
            : "An error occurred during registration"
        );
      } else {
        setApiResError("An error occurred during registration. Please try again.");
      }
    }
  };
  const countries = [
    { code: "+234", name: "Nigeria" },
    { code: "+91", name: "India" },
    { code: "+1", name: "USA" },
    { code: "+44", name: "UK" },
    { code: "+81", name: "Japan" },
    { code: "+61", name: "Australia" },
    { code: "+49", name: "Germany" },
    { code: "+86", name: "China" },
    { code: "+33", name: "France" },
    { code: "+55", name: "Brazil" },
  ];

  return (
    <>
      <div className="mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
        {/* Sign in section */}
        <form
          className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]"
          onSubmit={handleSubmit(handleVendorRegister)}
          noValidate
        >
          <h4 className="mb-2.5 mt-5 text-4xl font-bold text-navy-700 dark:text-white">Sign Up</h4>
          <div className="text-red-600">{apiResError}</div>
          <InputField
            control={control}
            name={SignUpFormFieldsName.FIRST_NAME}
            variant="auth"
            extra="mb-3"
            label="First Name*"
            placeholder="Enter your first name"
            id="firstName"
            type="text"
          />
          <InputField
            control={control}
            name={SignUpFormFieldsName.LAST_NAME}
            variant="auth"
            extra="mb-3"
            label="Last Name*"
            placeholder="Enter your last name"
            id="lastName"
            type="text"
          />
          <InputField
            control={control}
            name={SignUpFormFieldsName.EMAIL}
            variant="auth"
            extra="mb-3"
            label="Email*"
            placeholder="mail@mail.com"
            id="email"
            type="text"
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Phone No*
            </label>
            <div className="flex items-center gap-2">
              <SelectField
                control={control}
                name={SignUpFormFieldsName.COUNTRY_CODE}
                className="mr-2 w-full"
                options={countries.map((country) => ({
                  value: country.code,
                  label: `${country.code} (${country.name})`,
                }))}
              />
              <InputField
                control={control}
                name={SignUpFormFieldsName.PHONE}
                id="phone"
                type="tel"
                placeholder="Enter your number"
                extra="w-3/4"
              />
            </div>
          </div>

          <InputField
            control={control}
            name={SignUpFormFieldsName.PASSWORD}
            variant="auth"
            extra="mb-3"
            label="Password*"
            placeholder="Min. 8 characters"
            id="password"
            type="password"
          />
          <ul className="mb-2 list-disc pl-5 text-sm text-gray-700">
            <li>At least 8 characters long</li>
            <li>At least 1 uppercase letter (A–Z)</li>
            <li>At least 1 lowercase letter (a–z)</li>
            <li>At least 1 number (0–9)</li>
            <li>At least 1 special character (e.g., !@#$%^&*)</li>
          </ul>
          {/* <div className="mb-4 flex items-center justify-between px-2">
          <div className="flex items-center">
            <Checkbox />
            <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
              Keep me logged In
            </p>
          </div>
          <a
            className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
            href=" "
          >
            Forgot Password?
          </a>
        </div> */}
          <button
            type="submit"
            // disabled={!formState.isValid} // Disable button if form is not valid
            className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            Next
          </button>

          {/* <div className="mt-4">
          <span className=" text-sm font-medium text-navy-700 dark:text-gray-600">
            Not registered yet?
          </span>
          <a
            href=" "
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Create an account
          </a>
        </div> */}
        </form>
      </div>
    </>
  );
}
